import { onDelegatedEvent, tryJsonParse, viewTransition } from '../functions.js';

/**
 * Usage:
 * 
 * <conditional-render>
 * 
 *      <input type="checkbox" id="myCheckbox" />
 *      <input type="radio" name="myradio" value="0" />
 *      <input type="radio" name="myradio" value="1" />
 *      <input type="text" name="mytext" />
 * 
 *      Show/hide:
 *          <div x:if="qs('#myCheckbox').checked">Show when #myCheckbox is checked</div>
 *          <div x:if="qs('input[name=myradio][value=\'0\']').checked">Show when myradio=0 is checked</div>
 *          <div x:if="qs('input[name=myradio][value=\'1\']').checked">Show when myradio=1 is checked</div>
 *          <div x:if="qs('input[name=mytext]').value === 'hola'">Show when mytext="hola"</div>
 * 
 *      Add/remove attribute:
 *          <div x:if="qs('#myCheckbox').checked" x:attribute="data-checked">Add data-checked when #myCheckbox is checked</div>
 * 
 *      Add/remove attribute with value:
 *         <div x:if="qs('#myCheckbox').checked" x:attribute="placeholder" x:attribute-value="Custom placeholder">Add placeholder="Custom placeholder" when #myCheckbox is checked</div>
 *  
 *      Add/remove class
 *          <div x:if="qs('#myCheckbox').checked" x:class="checked">Add .checked when #myCheckbox is checked</div>
 * 
 * </conditional-render>
 * 
 * Whatever is inside x:if is execute as Javascript inside the <conditional-render> element context. In the context this === <conditional-render>.
 * the "qs(" string is replaced by "this.querySelector(" to allow for easy query children.
 * 
 * @since 2024-03-26 Evento sl-change (para <sl-switch />)
 * @since 2024-05-01 Converted to real custom element
 * @since 2024-05-01 Conditional class and attribute
 * @since 2024-07-10 Optional attribute value
 */

export default class ConditionalRenderElement extends HTMLElement {

    connectedCallback() {

        let qsa = this.querySelectorAll.bind(this);

        qsa('*[x\\:if]').forEach((subelm) => {

            let events = ['change', 'sl-change'];

            events.forEach((eventName) => {
                this.addEventListener(eventName, () => {
                    this.#toggleIf(subelm);
                });
            });

            this.#toggleIf(subelm);
        });

    }

    #show(elm) {
        viewTransition(() => {
            elm.style.display = '';
        });
    }

    #hide(elm) {
        viewTransition(() => {
            elm.style.display = 'none';
        });
    }

    #executeConditionFunction(conditionString) {

        conditionString = conditionString.replace(/qs\(/, 'this.querySelector(');
        conditionString = 'return ' + conditionString;

        // https://esbuild.github.io/content-types/#direct-eval
        let result = ((new Function(conditionString)).bind(this))();

        return result;
    }

    #toggleIf(targetSubelement) {

        let ifConditionString = targetSubelement.getAttribute('x:if');

        let attribute = targetSubelement.getAttribute('x:attribute');
        let attributeValue = targetSubelement.getAttribute('x:attribute-value') ?? '';
        let className = targetSubelement.getAttribute('x:class');
        
        let result = this.#executeConditionFunction(ifConditionString);

        if (attribute) {
            if (result) {
                targetSubelement.setAttribute(attribute, attributeValue);
            } else {
                targetSubelement.removeAttribute(attribute);
            }
        } else if (className) {
            if (result) {
                targetSubelement.classList.add(className);
            } else {
                targetSubelement.classList.remove(className);
            }
        } else {
            if (result) {
                this.#show(targetSubelement);
            } else {
                this.#hide(targetSubelement);
            }
        }


    }

};

window.customElements.define('conditional-render', ConditionalRenderElement);